// コーポレート(建設会社風)テンプレート。examples/template2.html が原案。
// 変数は fields で宣言し、body はその値(v)を data-bind / data-img 付きで描く。

import {
  type BuildOpts,
  type Field,
  type TemplateDef,
  type Values,
  formatText,
  imageSlot,
  seg,
} from "./shared";

const fields: Field[] = [
  // カラー
  {
    key: "brandColor",
    label: "ブランドカラー",
    group: "カラー",
    kind: "color",
    cssVar: "--brand",
    onVar: "--on-brand",
    default: "#2563eb",
  },
  {
    key: "accentColor",
    label: "アクセントカラー",
    group: "カラー",
    kind: "color",
    cssVar: "--accent",
    onVar: "--on-accent",
    default: "#f59e0b",
  },
  {
    key: "baseColor",
    label: "ベース背景色",
    group: "カラー",
    kind: "color",
    cssVar: "--base",
    onVar: "--on-base",
    default: "#ffffff",
  },
  // タイポグラフィ
  {
    key: "font",
    label: "フォント",
    group: "タイポグラフィ",
    kind: "font",
    cssVar: "--font",
    default: "gothic",
  },
  // コンテンツ
  {
    key: "eyebrow",
    label: "ヒーロー小見出し",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "創業40年の確かな技術",
  },
  {
    key: "headline",
    label: "見出し",
    group: "コンテンツ",
    kind: "text",
    format: "headline",
    default: "あなたのビジネスを**次のステージへ**",
  },
  {
    key: "subhead",
    label: "サブ見出し",
    group: "コンテンツ",
    kind: "textarea",
    format: "inline",
    default: "設計から施工、その先まで。建物づくりのすべてを、ワンストップで。",
  },
  {
    key: "ctaLabel",
    label: "CTA ボタン文言",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "無料で相談する",
  },
  {
    key: "tel",
    label: "電話番号",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "00-0000-0000",
  },
  {
    key: "stat1Val",
    label: "実績1 数値",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "1,200件+",
  },
  {
    key: "stat1Cap",
    label: "実績1 ラベル",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "施工実績",
  },
  {
    key: "stat2Val",
    label: "実績2 数値",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "40年",
  },
  {
    key: "stat2Cap",
    label: "実績2 ラベル",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "創業からの歩み",
  },
  {
    key: "stat3Val",
    label: "実績3 数値",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "98%",
  },
  {
    key: "stat3Cap",
    label: "実績3 ラベル",
    group: "コンテンツ",
    kind: "text",
    format: "plain",
    default: "顧客満足度",
  },
  {
    key: "worksTitle",
    label: "事例セクション見出し",
    group: "コンテンツ",
    kind: "text",
    format: "inline",
    default: "施工事例",
  },
  {
    key: "work1Cat",
    label: "事例1 カテゴリ",
    group: "コンテンツ",
    kind: "text",
    format: "inline",
    default: "商業施設",
  },
  {
    key: "work1Title",
    label: "事例1 タイトル",
    group: "コンテンツ",
    kind: "text",
    format: "inline",
    default: "複合商業ビル 新築工事",
  },
  {
    key: "work2Cat",
    label: "事例2 カテゴリ",
    group: "コンテンツ",
    kind: "text",
    format: "inline",
    default: "住宅",
  },
  {
    key: "work2Title",
    label: "事例2 タイトル",
    group: "コンテンツ",
    kind: "text",
    format: "inline",
    default: "注文住宅 設計・施工",
  },
  {
    key: "work3Cat",
    label: "事例3 カテゴリ",
    group: "コンテンツ",
    kind: "text",
    format: "inline",
    default: "公共施設",
  },
  {
    key: "work3Title",
    label: "事例3 タイトル",
    group: "コンテンツ",
    kind: "text",
    format: "inline",
    default: "市民ホール 改修工事",
  },
  // 画像
  {
    key: "heroMain",
    label: "メイン画像",
    group: "画像",
    kind: "image",
    placeholder: "メイン画像(建築物・現場写真など)",
  },
  {
    key: "work1Img",
    label: "事例1 写真",
    group: "画像",
    kind: "image",
    placeholder: "事例写真 1",
  },
  {
    key: "work2Img",
    label: "事例2 写真",
    group: "画像",
    kind: "image",
    placeholder: "事例写真 2",
  },
  {
    key: "work3Img",
    label: "事例3 写真",
    group: "画像",
    kind: "image",
    placeholder: "事例写真 3",
  },
];

const css = `
.cwrap{max-width:1240px;margin:0 auto;padding-left:36px;padding-right:36px}
.eyebrow-line{display:inline-flex;align-items:center;gap:10px;font-size:13.5px;font-weight:800;letter-spacing:.14em}
.eyebrow-line .rule{width:36px;height:2px;background:var(--accent);display:inline-block}

/* Header (ヒーローに重ねる) */
.hdr{position:absolute;top:0;left:0;right:0;z-index:10}
.hdr-in{display:flex;justify-content:space-between;align-items:center;padding:26px 36px;max-width:1240px;margin:0 auto}
.logo{display:flex;align-items:center;gap:12px}
.logo-mark{width:34px;height:34px;background:var(--brand);display:flex;align-items:center;justify-content:center}
.logo-tri{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-bottom:14px solid var(--on-brand)}
.logo-name{font-weight:900;font-size:20px;letter-spacing:.02em;color:#fff;text-shadow:0 1px 8px rgba(0,0,0,.3)}
.hnav{display:flex;align-items:center;gap:34px}
.hnav a{color:#fff;text-decoration:none;font-size:14.5px;font-weight:700;text-shadow:0 1px 8px rgba(0,0,0,.3);white-space:nowrap}
.hnav a.nav-cta{background:var(--accent);color:var(--on-accent);font-weight:800;padding:11px 24px;border-radius:4px;text-shadow:none}

/* Hero */
.hero{position:relative;height:720px;background:#1e293b;overflow:hidden}
.img-slot.hero-bg{position:absolute;inset:0;z-index:0;background-color:#1e293b}
.hero-bg .img-ph{color:rgba(255,255,255,.78);border-color:rgba(255,255,255,.28)}
.hero-scrim{position:absolute;inset:0;z-index:1;background:linear-gradient(100deg,rgba(15,23,42,.72) 0%,rgba(15,23,42,.38) 55%,rgba(15,23,42,.05) 100%);pointer-events:none}
.hero-inner{position:relative;z-index:2;height:100%;display:flex;flex-direction:column;justify-content:center;pointer-events:none}
.hero-copy{max-width:640px}
.hero-copy .hl{color:var(--accent)}
.hero-eyebrow{color:#fff;margin-bottom:26px}
.hero-title{font-size:58px;line-height:1.32;font-weight:900;letter-spacing:-.02em;margin:0 0 22px;color:#fff;text-wrap:balance;text-shadow:0 2px 20px rgba(0,0,0,.35)}
.hero-sub{font-size:19px;color:rgba(255,255,255,.92);margin:0 0 40px;font-weight:500;text-shadow:0 1px 12px rgba(0,0,0,.35)}
.hero-actions{display:flex;gap:16px;align-items:center;flex-wrap:wrap;pointer-events:auto}
.btn{display:inline-flex;align-items:center;gap:10px;text-decoration:none;font-weight:800;border-radius:4px}
.btn-arrow{font-size:19px}
.btn-accent{background:var(--accent);color:var(--on-accent);padding:17px 38px;font-size:17px;box-shadow:0 12px 28px -8px rgba(0,0,0,.4)}
.btn-line{color:#fff;font-weight:700;font-size:15.5px;padding:17px 10px;border-bottom:2px solid rgba(255,255,255,.5)}
.hero-stats{position:absolute;right:36px;bottom:0;z-index:3;background:var(--brand);color:var(--on-brand);display:flex}
.hero-stats .stat{padding:22px 30px;border-right:1px solid color-mix(in srgb, var(--on-brand) 20%, transparent)}
.hero-stats .stat:last-child{border-right:none}
.hero-stats .num{font-size:26px;font-weight:900;line-height:1.3}
.hero-stats .cap{font-size:12px;font-weight:700;opacity:.85}

/* Section 共通 */
.sec-head{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;flex-wrap:wrap;margin-bottom:40px}
.sec-title{font-size:38px;font-weight:900;letter-spacing:-.02em;margin:0;color:var(--on-base)}
.sec-more{color:var(--brand);text-decoration:none;font-weight:700;font-size:15px;border-bottom:2px solid var(--brand);padding-bottom:2px}

/* Works */
.works{background:var(--base)}
.works-in{padding:96px 36px 40px;max-width:1240px;margin:0 auto}
.works-eyebrow{color:var(--brand);margin-bottom:12px}
.works-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr;gap:20px}
.work-img{width:100%;height:380px;border-radius:6px}
.work-meta{padding:16px 4px 0}
.work-cat{font-size:12.5px;font-weight:800;color:var(--accent);letter-spacing:.08em;margin-bottom:4px}
.work-title{font-size:17px;font-weight:800;color:var(--on-base)}

/* Strengths */
.strengths{background:var(--base)}
.strengths-in{padding:80px 36px 96px;max-width:1240px;margin:0 auto}
.strengths-head{text-align:center;margin-bottom:56px}
.strengths-eyebrow{color:var(--brand);justify-content:center;margin-bottom:12px}
.strengths-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:26px}
.st-card{border-top:3px solid var(--brand);background:var(--surface);padding:34px 30px}
.st-no{font-size:14px;font-weight:900;color:var(--brand);letter-spacing:.1em;margin-bottom:16px}
.st-card h3{font-size:20px;font-weight:800;margin:0 0 12px;color:var(--on-base)}
.st-card p{font-size:15px;color:var(--muted);margin:0;font-weight:500}

/* CTA band */
.band{background:var(--brand)}
.band-in{max-width:1240px;margin:0 auto;padding:84px 36px;text-align:center}
.band-title{font-size:36px;font-weight:900;letter-spacing:-.02em;color:var(--on-brand);margin:0 0 14px;text-wrap:balance}
.band-sub{font-size:17px;color:color-mix(in srgb, var(--on-brand) 82%, transparent);margin:0 0 36px;font-weight:500}
.band-actions{display:flex;gap:16px;justify-content:center;align-items:center;flex-wrap:wrap}
.btn-tel{color:var(--on-brand);font-weight:800;font-size:17px;padding:17px 26px;border:1.5px solid color-mix(in srgb, var(--on-brand) 60%, transparent);border-radius:4px}

/* Footer */
.ft{background:#0f172a;color:#94a3b8}
.ft-in{max-width:1240px;margin:0 auto;padding:52px 36px 36px}
.ft-top{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:32px;padding-bottom:32px;border-bottom:1px solid #1e293b}
.ft-brand{max-width:320px}
.ft-logo{display:flex;align-items:center;gap:11px;margin-bottom:14px}
.ft-logo .logo-mark{width:30px;height:30px}
.ft-logo .logo-tri{border-left-width:8px;border-right-width:8px;border-bottom-width:12px;border-bottom-color:#fff}
.ft-name{font-weight:900;font-size:18px;color:#fff}
.ft-desc{font-size:14px;margin:0;line-height:1.7}
.ft-cols{display:flex;gap:64px;flex-wrap:wrap}
.ft-h{color:#fff;font-weight:700;font-size:14px;margin-bottom:14px}
.ft-links{display:flex;flex-direction:column;gap:10px}
.ft-links a{color:#94a3b8;text-decoration:none;font-size:14px}
.ft-copy{padding-top:24px;font-size:13px}

@media(max-width:920px){
  .works-grid{grid-template-columns:1fr}
  .strengths-grid{grid-template-columns:1fr}
  .hero-title{font-size:40px}
  .hero-stats{display:none}
  .hnav a:not(.nav-cta){display:none}
}`;

// 私たちの強み(静的コピー)。
const STRENGTHS: { no: string; title: string; body: string }[] = [
  {
    no: "01",
    title: "設計から施工まで一貫対応",
    body: "自社の設計士と施工チームが最初から最後まで伴走。窓口がひとつだから、意思疎通のズレがありません。",
  },
  {
    no: "02",
    title: "確かな品質管理",
    body: "全工程で第三者検査を実施し、記録をすべて開示。創業40年で培った品質基準を守り続けています。",
  },
  {
    no: "03",
    title: "引き渡し後も、ずっと",
    body: "定期点検と24時間の緊急対応窓口で、竣工後も建物を見守ります。長期保証制度もご用意しています。",
  },
];

function body(v: Values, _opts: BuildOpts): string {
  const cta = () =>
    `<a href="#cta" class="btn btn-accent"><span data-bind="ctaLabel">${formatText(v.ctaLabel ?? "", "plain")}</span><span class="btn-arrow">→</span></a>`;

  const heroBg = imageSlot({
    key: "heroMain",
    placeholder: "メイン画像(建築物・現場写真など)",
    className: "hero-bg",
    url: v.heroMain,
  });

  const stats = [1, 2, 3]
    .map(
      (n) =>
        `<div class="stat"><div class="num" data-bind="stat${n}Val">${formatText(v[`stat${n}Val`] ?? "", "plain")}</div><div class="cap" data-bind="stat${n}Cap">${formatText(v[`stat${n}Cap`] ?? "", "plain")}</div></div>`,
    )
    .join("");

  const works = [1, 2, 3]
    .map(
      (n) => `<div>
        ${imageSlot({ key: `work${n}Img`, placeholder: `事例写真 ${n}`, className: "work-img", url: v[`work${n}Img`] })}
        <div class="work-meta">
          <div class="work-cat" data-bind="work${n}Cat">${formatText(v[`work${n}Cat`] ?? "", "inline")}</div>
          <div class="work-title bdx" data-bind="work${n}Title">${formatText(v[`work${n}Title`] ?? "", "inline")}</div>
        </div>
      </div>`,
    )
    .join("");

  const strengths = STRENGTHS.map(
    (s) => `<div class="st-card">
        <div class="st-no">${s.no}</div>
        <h3 class="bdx">${seg(s.title)}</h3>
        <p class="bdx">${seg(s.body)}</p>
      </div>`,
  ).join("");

  return `
<!-- Header -->
<header class="hdr"><div class="hdr-in">
  <div class="logo">
    <div class="logo-mark"><div class="logo-tri"></div></div>
    <span class="logo-name">LP Design 建設</span>
  </div>
  <nav class="hnav">
    <a href="#works">施工事例</a>
    <a href="#features">私たちの強み</a>
    <a href="#company">会社概要</a>
    <a href="#cta" class="nav-cta">無料相談</a>
  </nav>
</div></header>

<!-- Hero -->
<section class="hero">
  ${heroBg}
  <div class="hero-scrim"></div>
  <div class="cwrap hero-inner">
    <div class="hero-copy">
      <div class="eyebrow-line hero-eyebrow"><span class="rule"></span><span data-bind="eyebrow">${formatText(v.eyebrow ?? "", "plain")}</span></div>
      <h1 class="hero-title bdx" data-bind="headline">${formatText(v.headline ?? "", "headline")}</h1>
      <p class="hero-sub bdx" data-bind="subhead">${formatText(v.subhead ?? "", "inline")}</p>
      <div class="hero-actions">
        ${cta()}
        <a href="#works" class="btn btn-line">施工事例を見る</a>
      </div>
    </div>
  </div>
  <div class="hero-stats">
    ${stats}
  </div>
</section>

<!-- Works -->
<section id="works" class="works">
  <div class="works-in">
    <div class="sec-head">
      <div>
        <div class="eyebrow-line works-eyebrow"><span class="rule"></span>WORKS</div>
        <h2 class="sec-title bdx" data-bind="worksTitle">${formatText(v.worksTitle ?? "", "inline")}</h2>
      </div>
      <a href="#" class="sec-more">すべての事例を見る →</a>
    </div>
    <div class="works-grid">
      ${works}
    </div>
  </div>
</section>

<!-- Strengths -->
<section id="features" class="strengths">
  <div class="strengths-in">
    <div class="strengths-head">
      <div class="eyebrow-line strengths-eyebrow"><span class="rule"></span>STRENGTH</div>
      <h2 class="sec-title">私たちの強み</h2>
    </div>
    <div class="strengths-grid">
      ${strengths}
    </div>
  </div>
</section>

<!-- CTA band -->
<section id="cta" class="band">
  <div class="band-in">
    <h2 class="band-title bdx">${seg("建物づくりの第一歩を、私たちと。")}</h2>
    <p class="band-sub bdx">${seg("ご相談・お見積りは無料です。まずはお気軽にお問い合わせください。")}</p>
    <div class="band-actions">
      ${cta()}
      <a href="tel:0000000000" class="btn btn-tel">☎ <span data-bind="tel">${formatText(v.tel ?? "", "plain")}</span></a>
    </div>
  </div>
</section>

<!-- Footer -->
<footer class="ft">
  <div class="ft-in">
    <div class="ft-top">
      <div class="ft-brand">
        <div class="ft-logo">
          <div class="logo-mark"><div class="logo-tri"></div></div>
          <span class="ft-name">LP Design 建設</span>
        </div>
        <p class="ft-desc bdx">${seg("設計から施工、アフターサポートまで。地域とともに40年、確かな建物づくりを続けています。")}</p>
      </div>
      <div class="ft-cols">
        <div>
          <div class="ft-h">事業内容</div>
          <div class="ft-links"><a href="#works">施工事例</a><a href="#features">私たちの強み</a><a href="#">建築サービス</a></div>
        </div>
        <div>
          <div class="ft-h">会社情報</div>
          <div class="ft-links"><a href="#company">会社概要</a><a href="#cta">お問い合わせ</a><a href="#">採用情報</a></div>
        </div>
      </div>
    </div>
    <div class="ft-copy">© 2026 Aozora Construction Co., Ltd. All rights reserved.</div>
  </div>
</footer>`;
}

export const corporateTemplate: TemplateDef = {
  id: "corporate",
  label: "コーポレート",
  fields,
  css,
  body,
};
